import * as React from 'react';
import { Star, Clock, ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { CostCentre } from '@/types/timesheet';
import { useCostCentreSelector } from '@/hooks/use-cost-centre-selector';
import { CostCentreSearchModal } from './CostCentreSearchModal';
import { Separator } from '@/components/ui/separator';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar, ScrollAreaThumb } from '@/components/ui/scroll-area';

interface CostCentreSelectorProps {
  costCentres: CostCentre[];
  value?: string;
  onChange: (costCentreId: string) => void;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  isSubmitted?: boolean;
  /** When false, the Favourites section is hidden in both the dropdown and the full search modal (e.g. approver screens) */
  showFavourites?: boolean;
  // Server-side pagination props (optional - for future use)
  onLoadMore?: () => void;
  hasMore?: boolean;
  remainingCount?: number;
  isLoadingMore?: boolean;
  // Server-side search props (optional - for future use)
  onServerSearch?: (query: string) => void;
  isSearching?: boolean;
  totalCount?: number;
}

export function CostCentreSelector({
  costCentres,
  value,
  onChange,
  disabled,
  placeholder = 'Select cost centre...',
  required = false,
  isSubmitted = false,
  showFavourites = true,
  // Server-side pagination props (optional - for future use)
  onLoadMore,
  hasMore,
  remainingCount,
  isLoadingMore,
  // Server-side search props (optional - for future use)
  onServerSearch,
  isSearching,
  totalCount,
}: CostCentreSelectorProps) {
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const {
    enrichedCostCentres,
    favouriteCostCentres,
    recentCostCentres,
    toggleFavourite,
    recordCostCentreAccess,
    isFavourite,
  } = useCostCentreSelector(costCentres);

  const selectedCostCentre = enrichedCostCentres.find((cc) => cc.id === value);

  // Filter cost centres based on search query
  const filteredCostCentres = React.useMemo(() => {
    if (!searchQuery.trim()) return enrichedCostCentres;
    
    const query = searchQuery.toLowerCase();
    return enrichedCostCentres.filter(
      (cc) =>
        cc.name.toLowerCase().includes(query) ||
        cc.code.toLowerCase().includes(query)
    );
  }, [enrichedCostCentres, searchQuery]);

  // Get filtered favourites and recent
  const filteredFavourites = React.useMemo(() => {
    if (!searchQuery.trim()) return favouriteCostCentres.slice(0, 5);
    return favouriteCostCentres.filter((cc) =>
      filteredCostCentres.some((fcc) => fcc.id === cc.id)
    );
  }, [favouriteCostCentres, filteredCostCentres, searchQuery]);

  const filteredRecent = React.useMemo(() => {
    if (!searchQuery.trim()) return recentCostCentres.slice(0, 5);
    return recentCostCentres.filter((cc) =>
      filteredCostCentres.some((fcc) => fcc.id === cc.id)
    );
  }, [recentCostCentres, filteredCostCentres, searchQuery]);

  // Get additional filtered cost centres (not in favourites or recent)
  const additionalCostCentres = React.useMemo(() => {
    if (!searchQuery.trim()) return [];
    
    const favouriteIds = new Set(filteredFavourites.map((cc) => cc.id));
    const recentIds = new Set(filteredRecent.map((cc) => cc.id));
    
    return filteredCostCentres
      .filter((cc) => !favouriteIds.has(cc.id) && !recentIds.has(cc.id))
      .slice(0, 10);
  }, [filteredCostCentres, filteredFavourites, filteredRecent, searchQuery]);

  // Handle cost centre selection
  const handleSelectCostCentre = React.useCallback(
    (costCentreId: string) => {
      onChange(costCentreId);
      recordCostCentreAccess(costCentreId);
      setDropdownOpen(false);
    },
    [onChange, recordCostCentreAccess]
  );

  // Handle modal open
  const handleOpenModal = React.useCallback(() => {
    setModalOpen(true);
    setDropdownOpen(false);
    setSearchQuery(''); // Reset search when opening modal
  }, []);

  // Reset search when dropdown closes
  React.useEffect(() => {
    if (!dropdownOpen) {
      setSearchQuery('');
    } else {
      // Focus search input when dropdown opens
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [dropdownOpen]);

  return (
    <>
      <Popover open={!disabled && dropdownOpen} onOpenChange={(open) => !disabled && setDropdownOpen(open)}>
        <PopoverTrigger asChild>
          <div className="relative">
            <Input
              ref={inputRef}
              value={
                selectedCostCentre
                  ? `${selectedCostCentre.name} (${selectedCostCentre.code})`
                  : ''
              }
              placeholder={placeholder}
              readOnly
              disabled={disabled}
              className={cn(
                'cursor-pointer pr-10',
                required && !value && !isSubmitted && 'border-destructive focus:ring-destructive',
                disabled && 'cursor-not-allowed'
              )}
              onClick={() => !disabled && setDropdownOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  !disabled && setDropdownOpen(true);
                }
                // Ctrl/Cmd + K to open modal
                if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                  e.preventDefault();
                  !disabled && handleOpenModal();
                }
              }}
              aria-label="Select cost centre"
              aria-expanded={dropdownOpen}
              role="combobox"
            />
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50" />
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="p-0"
          style={{ width: 'var(--radix-popover-trigger-width)', minWidth: '300px' }}
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* Search Input */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                type="text"
                placeholder="Search cost centres..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 pr-8 h-9"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          <ScrollArea className="max-h-[400px]">
            <ScrollAreaViewport className="max-h-[400px] p-2">
              {/* Favourites Section */}
              {showFavourites && filteredFavourites.length > 0 && (
                <div className="mb-3">
                  <div className="px-2 py-1.5 text-xs font-bold text-muted-foreground flex items-center gap-1.5">
                    <Star className="h-3.5 w-3.5" />
                    FAVOURITES
                  </div>
                  <div className="space-y-0.5">
                    {filteredFavourites.map((costCentre) => (
                      <button
                        key={costCentre.id}
                        onClick={() => handleSelectCostCentre(costCentre.id)}
                        className={cn(
                          'w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-accent transition-colors text-left',
                          value === costCentre.id && 'bg-accent'
                        )}
                      >
                        <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400 shrink-0" />
                        <span className="flex-1 truncate">
                          {costCentre.name}{' '}
                          <span className="text-muted-foreground">
                            ({costCentre.code})
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Recently Used Section */}
              {filteredRecent.length > 0 && (
                <div className="mb-3">
                  <div className="px-2 py-1.5 text-xs font-bold text-muted-foreground flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    RECENTLY USED
                  </div>
                  <div className="space-y-0.5">
                    {filteredRecent.map((costCentre) => (
                      <button
                        key={costCentre.id}
                        onClick={() => handleSelectCostCentre(costCentre.id)}
                        className={cn(
                          'w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-accent transition-colors text-left',
                          value === costCentre.id && 'bg-accent'
                        )}
                      >
                        <span className="flex-1 truncate">
                          {costCentre.name}{' '}
                          <span className="text-muted-foreground">
                            ({costCentre.code})
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Additional Search Results */}
              {searchQuery && additionalCostCentres.length > 0 && (
                <div className="mb-3">
                  <div className="px-2 py-1.5 text-xs font-bold text-muted-foreground flex items-center gap-1.5">
                    <Search className="h-3.5 w-3.5" />
                    OTHER RESULTS
                  </div>
                  <div className="space-y-0.5">
                    {additionalCostCentres.map((costCentre) => (
                      <button
                        key={costCentre.id}
                        onClick={() => handleSelectCostCentre(costCentre.id)}
                        className={cn(
                          'w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-accent transition-colors text-left',
                          value === costCentre.id && 'bg-accent'
                        )}
                      >
                        <span className="flex-1 truncate">
                          {costCentre.name}{' '}
                          <span className="text-muted-foreground">
                            ({costCentre.code})
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* No Results Message */}
              {searchQuery && filteredFavourites.length === 0 && filteredRecent.length === 0 && additionalCostCentres.length === 0 && (
                <div className="px-2 py-8 text-center text-sm text-muted-foreground">
                  <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No cost centres found matching "{searchQuery}"</p>
                  <p className="text-xs mt-1">Try a different search term</p>
                </div>
              )}

              {/* Empty State (no search) */}
              {!searchQuery && filteredRecent.length === 0 && (showFavourites ? filteredFavourites.length === 0 : true) && (
                <div className="px-2 py-8 text-center text-sm text-muted-foreground">
                  <p>{showFavourites ? 'No favourites or recent cost centres' : 'No recently used cost centres'}</p>
                  <p className="text-xs mt-1">Use the search above or click "Show More"</p>
                </div>
              )}
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical">
              <ScrollAreaThumb />
            </ScrollAreaScrollbar>
          </ScrollArea>

          <Separator className="my-0" />
          <div className="p-2">
            <Button
              variant="ghost"
              className="w-full justify-center text-sm"
              onClick={handleOpenModal}
            >
              Show More...
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Full Search Modal */}
      <CostCentreSearchModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        costCentres={enrichedCostCentres}
        value={value}
        onSelect={handleSelectCostCentre}
        onToggleFavourite={toggleFavourite}
        isFavourite={isFavourite}
        showFavourites={showFavourites}
        // Server-side pagination props
        onLoadMore={onLoadMore}
        hasMore={hasMore}
        remainingCount={remainingCount}
        isLoadingMore={isLoadingMore}
        // Server-side search props
        onServerSearch={onServerSearch}
        isSearching={isSearching}
        totalCount={totalCount}
      />
    </>
  );
}
